import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Tooltip,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import {
  PageEdits,
  TextAnnotation,
  RectAnnotation,
  DEFAULT_FONT_SIZE,
  MIN_FONT_SIZE,
  MAX_FONT_SIZE,
  BOX_COLORS,
  DEFAULT_BOX_COLOR,
} from '../services/pdfEdit';
import { renderPdfPageForEditor, EditorPageRender } from '../services/pdfOrganizer';

interface PdfPageEditorProps {
  open: boolean;
  bytes: Uint8Array;
  pageNumber: number; // 1-based
  rotation: number; // extra rotation applied in the organizer
  fileName: string;
  initialEdits: PageEdits;
  onCancel: () => void;
  onSave: (edits: PageEdits) => void;
}

type Selection = { type: 'text' | 'rect'; id: string } | null;

interface DragState {
  mode: 'move' | 'resize';
  type: 'text' | 'rect';
  id: string;
  startX: number;
  startY: number;
  startXFrac: number;
  startYFrac: number;
  startWFrac: number;
  startHFrac: number;
}

const EDITOR_TARGET_WIDTH = 800;
const MIN_RECT_FRAC = 0.02;
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

let editCounter = 0;
const newId = (prefix: string) => `${prefix}-${Date.now()}-${++editCounter}`;

const PdfPageEditor: React.FC<PdfPageEditorProps> = ({
  open,
  bytes,
  pageNumber,
  rotation,
  fileName,
  initialEdits,
  onCancel,
  onSave,
}) => {
  const [render, setRender] = useState<EditorPageRender | null>(null);
  const [loading, setLoading] = useState(false);
  const [texts, setTexts] = useState<TextAnnotation[]>([]);
  const [rects, setRects] = useState<RectAnnotation[]>([]);
  const [selected, setSelected] = useState<Selection>(null);

  const dragRef = useRef<DragState | null>(null);
  const renderRef = useRef<EditorPageRender | null>(null);
  renderRef.current = render;

  // Load the page image and seed the edits whenever the editor opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setRender(null);
    setSelected(null);
    setTexts(initialEdits.texts.map((t) => ({ ...t })));
    setRects(initialEdits.rects.map((r) => ({ ...r })));
    renderPdfPageForEditor(bytes, pageNumber, rotation, EDITOR_TARGET_WIDTH)
      .then((result) => {
        if (!cancelled) setRender(result);
      })
      .catch((err) => console.error('Error rendering page for editor:', err))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, bytes, pageNumber, rotation, initialEdits]);

  const selectedText =
    selected?.type === 'text' ? texts.find((t) => t.id === selected.id) || null : null;
  const selectedRect =
    selected?.type === 'rect' ? rects.find((r) => r.id === selected.id) || null : null;

  const updateText = (id: string, patch: Partial<TextAnnotation>) =>
    setTexts((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const updateRect = (id: string, patch: Partial<RectAnnotation>) =>
    setRects((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const handleAddText = () => {
    const annotation: TextAnnotation = {
      id: newId('text'),
      text: 'New text',
      xFrac: 0.4,
      yFrac: 0.45,
      fontSize: DEFAULT_FONT_SIZE,
    };
    setTexts((prev) => [...prev, annotation]);
    setSelected({ type: 'text', id: annotation.id });
  };

  const handleAddBox = () => {
    const rect: RectAnnotation = {
      id: newId('rect'),
      xFrac: 0.375,
      yFrac: 0.45,
      wFrac: 0.25,
      hFrac: 0.08,
      color: DEFAULT_BOX_COLOR,
    };
    setRects((prev) => [...prev, rect]);
    setSelected({ type: 'rect', id: rect.id });
  };

  const handleDeleteSelected = () => {
    if (!selected) return;
    if (selected.type === 'text') {
      setTexts((prev) => prev.filter((t) => t.id !== selected.id));
    } else {
      setRects((prev) => prev.filter((r) => r.id !== selected.id));
    }
    setSelected(null);
  };

  const onDragMove = (e: PointerEvent) => {
    const drag = dragRef.current;
    const r = renderRef.current;
    if (!drag || !r) return;
    const dx = (e.clientX - drag.startX) / r.cssWidth;
    const dy = (e.clientY - drag.startY) / r.cssHeight;

    if (drag.mode === 'move') {
      if (drag.type === 'text') {
        updateText(drag.id, {
          xFrac: clamp(drag.startXFrac + dx, 0, 0.999),
          yFrac: clamp(drag.startYFrac + dy, 0, 0.999),
        });
      } else {
        updateRect(drag.id, {
          xFrac: clamp(drag.startXFrac + dx, 0, 1 - drag.startWFrac),
          yFrac: clamp(drag.startYFrac + dy, 0, 1 - drag.startHFrac),
        });
      }
    } else {
      // Resize (rectangles only): grow/shrink from the bottom-right corner.
      updateRect(drag.id, {
        wFrac: clamp(drag.startWFrac + dx, MIN_RECT_FRAC, 1 - drag.startXFrac),
        hFrac: clamp(drag.startHFrac + dy, MIN_RECT_FRAC, 1 - drag.startYFrac),
      });
    }
  };

  const onDragEnd = () => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', onDragEnd);
  };

  const beginDrag = (drag: DragState) => {
    dragRef.current = drag;
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', onDragEnd);
  };

  const startMoveText = (e: React.PointerEvent, id: string) => {
    e.preventDefault();
    setSelected({ type: 'text', id });
    const a = texts.find((t) => t.id === id);
    if (!a) return;
    beginDrag({
      mode: 'move',
      type: 'text',
      id,
      startX: e.clientX,
      startY: e.clientY,
      startXFrac: a.xFrac,
      startYFrac: a.yFrac,
      startWFrac: 0,
      startHFrac: 0,
    });
  };

  const startMoveRect = (e: React.PointerEvent, id: string) => {
    e.preventDefault();
    setSelected({ type: 'rect', id });
    const a = rects.find((r) => r.id === id);
    if (!a) return;
    beginDrag({
      mode: 'move',
      type: 'rect',
      id,
      startX: e.clientX,
      startY: e.clientY,
      startXFrac: a.xFrac,
      startYFrac: a.yFrac,
      startWFrac: a.wFrac,
      startHFrac: a.hFrac,
    });
  };

  const startResizeRect = (e: React.PointerEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setSelected({ type: 'rect', id });
    const a = rects.find((r) => r.id === id);
    if (!a) return;
    beginDrag({
      mode: 'resize',
      type: 'rect',
      id,
      startX: e.clientX,
      startY: e.clientY,
      startXFrac: a.xFrac,
      startYFrac: a.yFrac,
      startWFrac: a.wFrac,
      startHFrac: a.hFrac,
    });
  };

  const handleDone = () => onSave({ texts, rects });

  const pxPerPoint = render ? render.cssWidth / render.pageWidthPt : 1;

  return (
    <Dialog open={open} onClose={onCancel} fullScreen>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Toolbar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            p: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Button variant="contained" startIcon={<TextFieldsIcon />} onClick={handleAddText}>
            Add Text
          </Button>
          <Button variant="outlined" startIcon={<CropSquareIcon />} onClick={handleAddBox}>
            Add Box
          </Button>

          {selectedText && (
            <>
              <Divider orientation="vertical" flexItem />
              <TextField
                label="Text"
                size="small"
                value={selectedText.text}
                onChange={(e) => updateText(selectedText.id, { text: e.target.value })}
                sx={{ minWidth: 220 }}
              />
              <TextField
                label="Font size"
                type="number"
                size="small"
                value={selectedText.fontSize}
                onChange={(e) =>
                  updateText(selectedText.id, {
                    fontSize: clamp(
                      parseInt(e.target.value, 10) || DEFAULT_FONT_SIZE,
                      MIN_FONT_SIZE,
                      MAX_FONT_SIZE
                    ),
                  })
                }
                inputProps={{ min: MIN_FONT_SIZE, max: MAX_FONT_SIZE }}
                sx={{ width: 110 }}
              />
              <Tooltip title="Delete text">
                <IconButton color="error" onClick={handleDeleteSelected}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          {selectedRect && (
            <>
              <Divider orientation="vertical" flexItem />
              <Typography variant="body2" color="text.secondary">
                Box color
              </Typography>
              <Stack direction="row" spacing={0.75}>
                {BOX_COLORS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => updateRect(selectedRect.id, { color })}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      backgroundColor: color,
                      border:
                        selectedRect.color === color ? '2px solid #1976d2' : '1px solid #9e9e9e',
                    }}
                  />
                ))}
              </Stack>
              <Tooltip title="Delete box">
                <IconButton color="error" onClick={handleDeleteSelected}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }} noWrap>
            {fileName} · p{pageNumber}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button color="inherit" startIcon={<CloseIcon />} onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleDone}>
              Done
            </Button>
          </Stack>
        </Box>

        {/* Canvas area */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            backgroundColor: 'grey.300',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            p: 3,
          }}
          onClick={() => setSelected(null)}
        >
          {loading || !render ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 8 }}>
              <CircularProgress />
              <Typography>Loading page…</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                position: 'relative',
                width: render.cssWidth,
                height: render.cssHeight,
                boxShadow: 3,
                backgroundColor: '#fff',
                flexShrink: 0,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box
                component="img"
                src={render.dataUrl}
                alt={`Page ${pageNumber}`}
                sx={{ width: '100%', height: '100%', display: 'block', userSelect: 'none' }}
                draggable={false}
              />

              {/* Rectangles (drawn behind text). */}
              {rects.map((rect) => {
                const isSelected = selected?.type === 'rect' && selected.id === rect.id;
                return (
                  <Box
                    key={rect.id}
                    onPointerDown={(e) => startMoveRect(e, rect.id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelected({ type: 'rect', id: rect.id });
                    }}
                    sx={{
                      position: 'absolute',
                      left: rect.xFrac * render.cssWidth,
                      top: rect.yFrac * render.cssHeight,
                      width: rect.wFrac * render.cssWidth,
                      height: rect.hFrac * render.cssHeight,
                      backgroundColor: rect.color,
                      boxSizing: 'border-box',
                      border: isSelected ? '1px solid #1976d2' : '1px solid rgba(0,0,0,0.25)',
                      cursor: 'move',
                      touchAction: 'none',
                    }}
                  >
                    {isSelected && (
                      <Box
                        onPointerDown={(e) => startResizeRect(e, rect.id)}
                        sx={{
                          position: 'absolute',
                          right: -6,
                          bottom: -6,
                          width: 12,
                          height: 12,
                          borderRadius: '2px',
                          backgroundColor: '#1976d2',
                          cursor: 'nwse-resize',
                          touchAction: 'none',
                        }}
                      />
                    )}
                  </Box>
                );
              })}

              {/* Text (drawn on top). */}
              {texts.map((ann) => (
                <Box
                  key={ann.id}
                  onPointerDown={(e) => startMoveText(e, ann.id)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected({ type: 'text', id: ann.id });
                  }}
                  sx={{
                    position: 'absolute',
                    left: ann.xFrac * render.cssWidth,
                    top: ann.yFrac * render.cssHeight,
                    fontSize: `${ann.fontSize * pxPerPoint}px`,
                    lineHeight: 1,
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    color: '#000',
                    whiteSpace: 'pre',
                    cursor: 'move',
                    userSelect: 'none',
                    touchAction: 'none',
                    padding: '1px',
                    outline:
                      selected?.type === 'text' && selected.id === ann.id
                        ? '1px dashed #1976d2'
                        : '1px dashed transparent',
                    '&:hover': { outline: '1px dashed #90caf9' },
                  }}
                >
                  {ann.text || ' '}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default PdfPageEditor;
